import type { SchoolResult } from "@/services/schools";
import { FitComparison } from "./FitComparison";
import type { GolfFitAssessment } from "@/services/schoolFits";
import { SchoolBrandHeader } from "./SchoolBrandHeader";
import { SchoolFitSummary } from "./SchoolFitSummary";
import { Check, Plus, X } from "lucide-react";

interface SchoolSearchCardProps {
  school: SchoolResult;
  fit?: GolfFitAssessment | null;
  selected: boolean;
  playerScoringDifferential?: number | null;
  playerClassRank?: number | null;
  isInMySchools: boolean;
  membershipLoading?: boolean;
  onOpen: () => void;
  onToggleMySchools: () => void;
}

export function SchoolSearchCard({
  school,
  fit,
  selected,
  playerScoringDifferential,
  playerClassRank,
  isInMySchools,
  membershipLoading = false,
  onOpen,
  onToggleMySchools,
}: SchoolSearchCardProps) {
  const playerDifferential =
    fit?.evidence?.playerScoringDifferential ?? playerScoringDifferential ?? null;
  const programDifferential =
    fit?.evidence?.programAverageScoringDifferential ??
    school.commit_score_stats?.avg_score_differential ??
    null;
  const programClassRank = school.commit_score_stats?.avg_class_rank;

  return (
    <article
      className={`min-w-0 overflow-hidden rounded-[18px] border bg-white shadow-[0_7px_20px_rgba(26,43,35,0.05)] transition duration-200 ${selected ? "border-primary ring-4 ring-primary/10" : "border-border hover:-translate-y-0.5 hover:border-border-strong hover:shadow-[0_14px_32px_rgba(26,43,35,0.1)]"}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpen();
          }
        }}
        className="block w-full text-left focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-primary/25"
        aria-label={`Open ${school.schoolName}`}
      >
        <SchoolBrandHeader school={school} fit={fit} variant="compact" />
        <div className="p-3.5">
          <FitComparison
            playerRank={fit?.evidence?.playerClassRank ?? playerClassRank ?? null}
            programRank={fit?.evidence?.programAverageClassRank ?? programClassRank ?? null}
            playerDifferential={playerDifferential}
            programDifferential={programDifferential}
          />
          <SchoolFitSummary
            markdown={fit?.summary}
            fallback={
              programClassRank != null
                ? `Recent commits average around class rank #${Math.round(programClassRank)}.`
                : "Assess your golf fit using your verified profile and this program’s recruiting data."
            }
            lines={2}
            className="mt-2.5 min-h-[34px] text-[11px] leading-[1.55] text-muted-foreground"
          />
        </div>
      </div>
      <button
        type="button"
        onClick={onToggleMySchools}
        disabled={membershipLoading}
        aria-pressed={isInMySchools}
        aria-label={`${isInMySchools ? "Remove" : "Add"} ${school.schoolName} ${isInMySchools ? "from" : "to"} My Schools`}
        className={`group/save flex h-11 w-full items-center justify-center gap-2 border-t text-[11px] font-extrabold transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-primary/25 disabled:cursor-wait disabled:opacity-60 ${
          isInMySchools
            ? "border-border bg-surface text-forest-soft hover:bg-warn-soft hover:text-warn"
            : "border-border bg-white text-primary-text hover:bg-lime-soft"
        }`}
      >
        {membershipLoading ? (
          "Updating…"
        ) : isInMySchools ? (
          <>
            <span className="inline-flex items-center gap-2 group-hover/save:hidden group-focus-visible/save:hidden">
              <Check className="h-4 w-4 text-primary-text" /> In My Schools
            </span>
            <span className="hidden items-center gap-2 group-hover/save:inline-flex group-focus-visible/save:inline-flex">
              <X className="h-4 w-4" /> Remove from My Schools
            </span>
          </>
        ) : (
          <>
            <Plus className="h-4 w-4" /> Add to My Schools
          </>
        )}
      </button>
    </article>
  );
}
